// EntitySheet.tsx — Pattern 1 (DRILL-THROUGH) for the v4 shell.
//
// A universal bottom Sheet driven by the entityStore stack. `window.__openEntity`
// pushes an entity; this component fetches it (live) from the PWA data API and
// renders a typed detail sheet. Every neighbour (parent milestone, child steps,
// cross-references) renders as a tappable `link-pill` that opens ITS sheet —
// making navigation a recursive graph descent.

import { useEffect, useState } from "react";
import { fetchEntity } from "../api/client.js";
import {
  getStack,
  subscribe,
  popEntity,
  closeEntities,
  type EntityRef,
} from "./entityStore.js";
import { pathTypeFromId, typeLabel } from "./entityTypes.js";
import { useTimeShift } from "./TimeShift.js";

const STATUS_CHIP: Record<string, string> = {
  complete: "ok",
  completed: "ok",
  integrated: "ok",
  merged: "ok",
  adopted: "ok",
  accepted: "ok",
  pass: "ok",
  active: "accent",
  in_progress: "accent",
  "in-progress": "accent",
  running: "accent",
  proposed: "warn",
  pending: "warn",
  triaged: "warn",
  conditional: "warn",
  pending_review: "warn",
  failed: "danger",
  error: "danger",
  rejected: "danger",
  cancelled: "danger",
  fail: "danger",
};

function statusChipClass(status: string): string {
  const cls = STATUS_CHIP[status?.toLowerCase()] ?? "";
  return cls ? `chip ${cls}` : "chip";
}

function openRef(type: string | null, id: string): void {
  const t = type ?? pathTypeFromId(id);
  if (t) window.__openEntity?.(t, id);
}

function LinkPill({ id, type }: { id: string; type?: string | null }) {
  const t = type ?? pathTypeFromId(id);
  return (
    <button
      className="link-pill"
      style={{ cursor: "pointer" }}
      disabled={!t}
      onClick={() => openRef(t, id)}
    >
      {id}
    </button>
  );
}

interface Neighbor {
  id: string;
  title?: string;
  status?: string;
  entity_type?: string;
}

function NeighborRow({ n }: { n: Neighbor }) {
  const t = pathTypeFromId(n.id);
  return (
    <button
      className="card"
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        cursor: t ? "pointer" : "default",
        marginBottom: 4,
      }}
      disabled={!t}
      onClick={() => openRef(t, n.id)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className="mono" style={{ color: "var(--accent)", fontSize: 10 }}>
          {n.id}
        </span>
        <span style={{ flex: 1, fontSize: 12 }}>{n.title ?? ""}</span>
        {n.status && <span className={statusChipClass(n.status)}>{n.status}</span>}
      </div>
    </button>
  );
}

function SheetBody({ entity }: { entity: Record<string, unknown> }) {
  const phase = entity.phase as string | null;
  const domain = entity.domain as string | null;
  const tags = (entity.tags as string[] | null) ?? [];
  const validFrom = entity.valid_from as string | null;
  const crossRefs = (entity.cross_refs as Neighbor[] | null) ?? [];
  const childSteps = (entity.child_steps as Neighbor[] | null) ?? [];
  const parentMilestone = entity.parent_milestone as Neighbor | null;

  // Description and owner — may come from top-level or metadata spread.
  const description =
    (entity.description as string | null) ??
    (entity.metadata as Record<string, unknown> | null)?.description as string | null ?? null;
  const owner =
    (entity.owner as string | null) ??
    (entity.metadata as Record<string, unknown> | null)?.owner as string | null ?? null;

  // "why blocked / next action" callout — surfaced from flattened metadata.
  const blockedBy =
    (entity.blockedBy as string | null) ?? (entity.blocked_by as string | null);
  const nextAction =
    (entity.next_action as string | null) ??
    (entity.nextAction as string | null) ??
    (entity.why as string | null);

  return (
    <div className="sheet-body">
      {(blockedBy || nextAction) && (
        <div
          className="sheet-section"
          style={{
            background: blockedBy ? "var(--warn-soft)" : "var(--accent-soft)",
            border: `1px solid ${blockedBy ? "var(--warn)" : "var(--accent-dim)"}`,
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: blockedBy ? "var(--warn)" : "var(--accent)",
              marginBottom: 4,
            }}
          >
            {blockedBy ? "Blocked" : "Next action"}
          </div>
          <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
            {blockedBy ?? nextAction}
          </div>
        </div>
      )}

      <div className="sheet-section">
        <h4>Metadata</h4>
        {phase && (
          <div className="sheet-row">
            <span className="k">Phase</span>
            <span className="v mono" style={{ fontSize: 11 }}>
              {phase}
            </span>
          </div>
        )}
        {domain && (
          <div className="sheet-row">
            <span className="k">Domain</span>
            <span className="v">{domain}</span>
          </div>
        )}
        {description && (
          <div className="sheet-row">
            <span className="k">Description</span>
            <span className="v" style={{ fontSize: 12, lineHeight: 1.5 }}>{description}</span>
          </div>
        )}
        {owner && (
          <div className="sheet-row">
            <span className="k mono" style={{ fontSize: 11 }}>Owner</span>
            <span className="v mono" style={{ fontSize: 11 }}>{owner}</span>
          </div>
        )}
        {validFrom && (
          <div className="sheet-row">
            <span className="k">Created</span>
            <span className="v mono" style={{ fontSize: 11 }}>
              {new Date(validFrom).toLocaleDateString()}
            </span>
          </div>
        )}
        {tags.length > 0 && (
          <div className="sheet-row">
            <span className="k">Tags</span>
            <span
              className="v"
              style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}
            >
              {tags.map((t) => (
                <span key={t} className="chip" style={{ fontSize: 10 }}>
                  {t}
                </span>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* Content fields — spec, summary, hypothesis, user story, acceptance criteria */}
      {(() => {
        const _meta = (entity.metadata as Record<string, unknown> | null) ?? {};
        const _specSrc = entity.spec ?? _meta.spec;
        // Only render spec as text if it's a string; if it's an object, extraction handles it below.
        const spec = _specSrc != null && typeof _specSrc !== "object"
          ? String(_specSrc)
          : null;
        const summary =
          (entity.summary as string | null) ??
          (_meta.summary as string | null) ?? null;
        const hypothesis =
          (entity.hypothesis as string | null) ??
          (_meta.hypothesis as string | null) ?? null;
        const userStory =
          (entity.userStory as string | null) ??
          (entity.user_story as string | null) ??
          (_meta.userStory as string | null) ??
          (_meta.user_story as string | null) ?? null;
        const acceptanceCriteria =
          (entity.acceptanceCriteria as string | null) ??
          (entity.acceptance_criteria as string | null) ??
          (_meta.acceptanceCriteria as string | null) ??
          (_meta.acceptance_criteria as string | null) ?? null;
        const deliverable =
          (entity.deliverable as string | null) ??
          (_meta.deliverable as string | null) ??
          ((typeof entity.spec === "object" && entity.spec != null) ? (entity.spec as Record<string,unknown>).deliverable as string | null : null) ?? null;
        const notes =
          (entity.notes as string | null) ??
          (_meta.notes as string | null) ?? null;

        const hasContent = spec || summary || hypothesis || userStory || acceptanceCriteria || deliverable || notes;
        if (!hasContent) return null;

        return (
          <div className="sheet-section">
            <h4>Content</h4>
            {summary && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Summary</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{summary}</span>
              </div>
            )}
            {spec && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Spec</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%", whiteSpace: "pre-wrap" }}>{spec}</span>
              </div>
            )}
            {hypothesis && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Hypothesis</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{hypothesis}</span>
              </div>
            )}
            {userStory && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">User Story</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{userStory}</span>
              </div>
            )}
            {acceptanceCriteria && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Acceptance Criteria</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{acceptanceCriteria}</span>
              </div>
            )}
            {deliverable && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Deliverable</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{deliverable}</span>
              </div>
            )}
            {notes && (
              <div className="sheet-row" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span className="k">Notes</span>
                <span className="v" style={{ fontSize: 12, lineHeight: 1.5, width: "100%" }}>{notes}</span>
              </div>
            )}
          </div>
        );
      })()}

      {parentMilestone && (
        <div className="sheet-section">
          <h4>Milestone</h4>
          <NeighborRow n={parentMilestone} />
        </div>
      )}

      {childSteps.length > 0 && (
        <div className="sheet-section">
          <h4>Steps ({childSteps.length})</h4>
          {childSteps.map((s) => (
            <NeighborRow key={s.id} n={s} />
          ))}
        </div>
      )}

      {crossRefs.length > 0 && (
        <div className="sheet-section">
          <h4>Related ({crossRefs.length})</h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {crossRefs.map((ref) => (
              <LinkPill key={ref.id} id={ref.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function Breadcrumb({ stack, currentDepth }: { stack: EntityRef[]; currentDepth: number }) {
  if (currentDepth <= 1) return null;
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        padding: '0 0 8px 0', borderBottom: '1px solid var(--border-soft)', marginBottom: 8,
      }}
    >
      {stack.slice(0, currentDepth - 1).map((ref) => (
        <span key={`${ref.type}/${ref.id}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            {typeLabel(ref.type)} · {ref.id}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>›</span>
        </span>
      ))}
      <span className="mono" style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>
        {typeLabel(stack[currentDepth - 1].type)} · {stack[currentDepth - 1].id}
      </span>
    </div>
  );
}

function ActiveSheet({ entityRef, depth, fullStack }: { entityRef: EntityRef; depth: number; fullStack: EntityRef[] }) {
  const { asOf } = useTimeShift();
  const [entity, setEntity] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntity(null);
    // In scrubbed mode the sheet shows the entity as it was at `asOf` (title,
    // status, description from the temporal archive); neighbours stay live.
    fetchEntity(entityRef.type, entityRef.id, asOf)
      .then((data) => {
        if (!cancelled) setEntity(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityRef.type, entityRef.id, asOf]);

  const id = (entity?.id as string | null) ?? entityRef.id;
  const title = entity?.title as string | null;
  const status = entity?.status as string | null;

  return (
    <>
      <div className="sheet-backdrop open" style={{ position: "fixed" }} onClick={closeEntities} />
      <div className="sheet open" style={{ position: "fixed" }}>
        <div className="sheet-handle" />
        <Breadcrumb stack={fullStack} currentDepth={depth} />
        <div className="sheet-head">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div className="eyebrow">
              {typeLabel(entityRef.type)} · {id}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {depth > 1 && (
                <button
                  onClick={popEntity}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--accent)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  ‹ Back
                </button>
              )}
              <button
                onClick={closeEntities}
                aria-label="Close"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-dim)",
                  fontSize: 16,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          </div>
          {title && (
            <h3 style={{ fontSize: 15, lineHeight: 1.35, marginTop: 6 }}>{title}</h3>
          )}
          {status && <span className={statusChipClass(status)}>{status}</span>}
          {typeof entity?.as_of === "string" && (
            <span className="chip warn" style={{ fontSize: 10, marginLeft: 6 }}>
              ◷ as of{" "}
              {new Date(entity.as_of).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>

        {loading && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            Loading…
          </div>
        )}
        {error && !loading && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: "var(--danger)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            ⚠ {error}
          </div>
        )}
        {entity && !loading && <SheetBody entity={entity} />}
      </div>
    </>
  );
}

/** Root-mounted drill-through sheet. Renders the top of the entityStore stack. */
export function EntitySheet() {
  const [stack, setStack] = useState<EntityRef[]>(getStack());
  useEffect(() => subscribe(() => setStack([...getStack()])), []);

  const top = stack[stack.length - 1];
  if (!top) return null;
  // Key by ref so switching entities re-fetches; depth drives the Back affordance.
  return <ActiveSheet key={`${top.type}/${top.id}`} entityRef={top} depth={stack.length} fullStack={stack} />;
}
